import { useEffect, useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Stack, useTheme } from '@mui/material'

import useFocus from './use-focus'
import LoaderMap from 'components/LoaderMap'
import { processRegularSites } from 'components/Cartography/LoaderMetrics'
import GoogleMap from 'components/Cartography'
import { OPERATION_ACCOUNTS_METRICS_BY_GEOLOC } from 'components/Cartography/operations-gql/models/query-gql'

type Site = {
  location: Position
  id: string
  sf_id: string
  name: string
}

type Position = {
  longitude: number
  latitude: number
}

type AccountSite = {
  site: Site
  available_level: string
  last_seen: string
  counting: {
    active_users: number
    line_ok: number
    line_unreachable: number
    accesspoint_ok: number
    accesspoint_unreachable: number
    switch_ok: number
    switch_unreachable: number
    chromecast_ok: number
    chromecast_unreachable: number
    tv_ok: number
    tv_unreachable: number
    esl_ok: number
    esl_unreachable: number
    firewall_ok: number
    firewall_unreachable: number
    wan_tx_bits: number
    wan_rx_bits: number
  }

}

const DEFAULT_POSITION: Position = { longitude: 2.3060, latitude: 48.8733 }

const getCurrentPosition = async (): Promise<Position> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(DEFAULT_POSITION)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({ longitude: position.coords.longitude, latitude: position.coords.latitude })
      },
      (err) => {
        const errorMessages: Record<number, string> = {
          1: '❌ PERMISSION_DENIED: L\'utilisateur a refusé la géolocalisation. Vérifie les permissions Chrome (🔒 > Paramètres du site > Localisation)',
          2: '❌ POSITION_UNAVAILABLE: Position indisponible. Vérifie: 1) Services de localisation macOS activés pour Chrome 2) Connexion réseau/GPS',
          3: '❌ TIMEOUT: Délai dépassé. Réessaie ou augmente le timeout',
        };
        console.error(errorMessages[err.code] || `❌ Erreur inconnue: ${err.message}`)

        resolve(DEFAULT_POSITION)
      },
      {
        enableHighAccuracy: false, // Essaie d'abord sans haute précision (plus rapide)
        timeout: 10000, // Augmente le timeout à 10s
        maximumAge: 60000 // Accepte une position en cache de max 1 minute
      }

    )
  })
}

type HomeInternalProps = {
  isExpanded: boolean
  onToggleExpand: () => void
}

type GeolocationProps = {
  active: boolean;
  coords: Position;
  onChange: (pos: Position) => void,
  onReset: () => void
}

const GoogleMapInternal = ({ isExpanded = false, onToggleExpand }: HomeInternalProps) => {
  const [isMapExtended, setIsMapExtended] = useState(isExpanded)
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null)
  const [position, setPosition] = useState<Position | null>(null)
  const {t} = useTranslation()
  const theme = useTheme()
  const { onFocus, loading } = useFocus()

  const { data } = useQuery<{ accounts: AccountSite[] }>(OPERATION_ACCOUNTS_METRICS_BY_GEOLOC, {
    variables: {
      longitude: position?.longitude,
      latitude: position?.latitude
    },
    skip: !position,
    fetchPolicy: 'cache-and-network'
  })

  const sites = useMemo(() => {
    if (!data?.accounts || data.accounts.length === 0) {
      return []
    }
    const filteredSites = data.accounts
      .filter(({ site }) => Boolean(site.location))
      .map(({ site, available_level }) => ({
        ...site,
        metrics: { available_level }
      }))
    return processRegularSites(filteredSites, data.accounts)
  }, [data])

  useEffect(() => {
    getCurrentPosition().then((pos) => {
      setCurrentPosition(pos)
      setPosition(pos)
    })
  }, [])

  if (!position) {
    return <LoaderMap />
  }

  return (
    <Stack sx={{flex: 1}}>
      <GoogleMap
        sites={sites}
        geolocation={
          {
            active: true,
            coords: {
              latitude: position.latitude,
              longitude: position.longitude
            },
            onReset: (): Position | null => {
              setPosition(currentPosition)
              return currentPosition
            },
            onChange: (position: Position): void => { setPosition(position) }
          } as GeolocationProps
        }
        isExpanded={isMapExtended}
        onMarkerClick={async (marker) => {
          await onFocus(marker.sf_id)
        }}
        onToggleExpand={() => {
          setIsMapExtended(!isMapExtended)
          onToggleExpand()
        }}
      />
      {
        loading && (
          <Stack sx={{
            position: 'absolute',
            left: 24,
            top: 86,
            borderRadius: 5,
            paddingLeft: 1,
            paddingRight: 1,
            color: theme.palette.grey[700],
            backgroundColor: 'white',
            height: 32,
            fontSize: '12px',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          >
            {t('home.internal.loading_site')}
          </Stack>
        )
      }
    </Stack>
  )
}

export default GoogleMapInternal